"use client"

import { useState } from "react"
import axios from "axios"
import { motion, AnimatePresence } from "framer-motion"
import {
  Calendar,
  Check,
  Clock,
  Link2,
  Mail,
  MapPin,
  MessageSquare,
  User,
  X,
  Video
} from "lucide-react"

const MeetingsListModal = ({
  isOpen,
  onClose,
  meetings,
  refreshMeetings
}) => {
  const [selectedMeeting, setSelectedMeeting] = useState(null)
  const [meetingLink, setMeetingLink] = useState("")
  const [message, setMessage] = useState("")
  const [status, setStatus] = useState("pending")
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // 1) Update meeting record in database
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/meetings/${selectedMeeting._id}`,
        {
          status,
          meetingLink: status === "approved" ? meetingLink : undefined,
          message: status === "rejected" ? message : undefined
        }
      )

      // 2) Send notification email
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/send-meeting-response`,
        {
          email: selectedMeeting.email,
          name: selectedMeeting.name,
          meetingType: selectedMeeting.meetingType,
          meetingDate: selectedMeeting.meetingDate,
          status,
          meetingLink:
            status === "approved" ? meetingLink : undefined,
          message: status === "rejected" ? message : undefined
        }
      )

      // 3) Refresh meetings list and reset selection
      refreshMeetings()
      setSelectedMeeting(null)
      setMeetingLink("")
      setMessage("")
      setStatus("pending")
    } catch (error) {
      console.error("Error updating meeting & sending email:", error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderMeetingLinkField = () => {
    if (status !== "approved") return null;

    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {selectedMeeting.meetingMode === "online" ? (
            <>
              <Video className="inline w-4 h-4 mr-1" />
              Meeting Link
            </>
          ) : (
            <>
              <MapPin className="inline w-4 h-4 mr-1" />
              Location Link
            </>
          )}
        </label>
        <input
          type="text"
          value={meetingLink}
          onChange={(e) => setMeetingLink(e.target.value)}
          placeholder={
            selectedMeeting.meetingMode === "online" 
              ? "Enter Zoom/Google Meet link" 
              : "Enter Google Maps location link"
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          required
        />
        <p className="mt-1 text-xs text-gray-500">
          {selectedMeeting.meetingMode === "online"
            ? "e.g. https://meet.google.com/abc-xyz-123"
            : "e.g. https://maps.google.com?q=Your+Business+Address"}
        </p>
      </div>
    )
  }

  const renderMeetingDetails = (meeting) => {
    return (
      <div className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="font-medium text-gray-900">
              {meeting.meetingType === "consultation"
                ? "Consultation"
                : "Ideation"}{" "}
              Meeting
            </h3>
            <p className="text-sm text-gray-500">
              {meeting.name} • {meeting.email}
            </p>
          </div>
          <span
            className={`px-2 py-1 text-xs rounded-full ${
              meeting.status === "approved"
                ? "bg-green-100 text-green-800"
                : meeting.status === "rejected"
                ? "bg-red-100 text-red-800"
                : "bg-blue-100 text-blue-800"
            }`}
          >
            {meeting.status || "pending"}
          </span>
        </div>
        <div className="mt-2 flex items-center text-sm text-gray-500">
          <Calendar className="mr-1 h-4 w-4" />
          {new Date(meeting.meetingDate).toLocaleDateString()} 
        </div>
        {meeting.status === "approved" && meeting.meetingLink && (
          <div className="mt-2 flex items-center text-sm">
            {meeting.meetingMode === "online" ? (
              <a
                href={meeting.meetingLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 flex items-center"
              >
                <Video className="mr-1 h-4 w-4" />
                Join Meeting
              </a>
            ) : (
              <a
                href={meeting.meetingLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 flex items-center"
              >
                <MapPin className="mr-1 h-4 w-4" />
                View Location
              </a>
            )}
          </div>
        )}
      </div>
    )
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      {/* backdrop catches outside clicks */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        {/* inner modal stops propagation */}
        <motion.div
          initial={{ scale: 0.95, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 20 }}
          className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {!selectedMeeting ? (
            <>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  Manage Meetings
                </h2>
                <button
                  onClick={onClose}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                {meetings.length === 0 ? (
                  <div className="text-center py-8">
                    <Clock className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-lg font-medium text-gray-900">
                      No meetings scheduled
                    </h3>
                    <p className="mt-1 text-gray-500">
                      All meeting requests will appear here.
                    </p>
                  </div>
                ) : (
                  meetings.map((meeting) => (
                    <div
                      key={meeting._id}
                      onClick={() => {
                        setSelectedMeeting(meeting)
                        setStatus(meeting.status || "pending")
                        setMeetingLink(meeting.meetingLink || "")
                        setMessage(meeting.message || "")
                      }}
                    >
                      {renderMeetingDetails(meeting)}
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  {selectedMeeting.meetingType === "consultation"
                    ? "Consultation"
                    : "Ideation"}{" "}
                  Meeting
                </h2>
                <button
                  onClick={() => setSelectedMeeting(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="space-y-4 mb-6">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <User className="text-gray-600" />
                    <div>
                      <p className="text-sm text-gray-500">Client</p>
                      <p className="font-medium">{selectedMeeting.name}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Mail className="text-gray-600" />
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-medium">{selectedMeeting.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Calendar className="text-gray-600" />
                    <div>
                      <p className="text-sm text-gray-500">
                        Requested Date
                      </p>
                      <p className="font-medium">
                        {new Date(
                          selectedMeeting.meetingDate
                        ).toLocaleDateString()}{" "}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    {selectedMeeting.meetingMode === "online" ? (
                      <Video className="text-gray-600" />
                    ) : (
                      <MapPin className="text-gray-600" />
                    )}
                    <div>
                      <p className="text-sm text-gray-500">Meeting Mode</p>
                      <p className="font-medium capitalize">
                        {selectedMeeting.meetingMode}
                      </p>
                    </div>
                  </div>

                  {renderMeetingLinkField()}

                  {status === "rejected" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <MessageSquare className="inline w-4 h-4 mr-1" />
                        Decline Message
                      </label>
                      <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Reason for declining..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows={3}
                        required
                      />
                    </div>
                  )}
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setStatus("approved")}
                    className={`flex-1 py-2 rounded-lg border ${
                      status === "approved"
                        ? "bg-green-100 border-green-500 text-green-700"
                        : "bg-gray-100 border-gray-300 text-gray-700"
                    }`}
                  >
                    <Check className="inline w-4 h-4 mr-1" />
                    Accept
                  </button>
                  <button
                    type="button"
                    onClick={() => setStatus("rejected")}
                    className={`flex-1 py-2 rounded-lg border ${
                      status === "rejected"
                        ? "bg-red-100 border-red-500 text-red-700"
                        : "bg-gray-100 border-gray-300 text-gray-700"
                    }`}
                  >
                    <X className="inline w-4 h-4 mr-1" />
                    Decline
                  </button>
                </div>

                <div className="mt-6">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-colors disabled:opacity-50"
                  >
                    {isSubmitting ? "Processing..." : "Submit Response"}
                  </button>
                </div>
              </form>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default MeetingsListModal